/** Mirrors VerificationStatus in fms_inherited/models/soa.py. */
export const ALL_VERIFIED = 3;

/** Mirrors EmailStatus in client_service/models.py. */
export type SoaEmailStatus = 'not_sent' | 'queued' | 'sent' | 'failed' | 'bounced';

export interface SoaDocument {
    id: number;
    period_end: string;
    file: string;
    file_name: string | null;
    original_filename: string;
    extracted_name: string;
    client_master: number | null;
    client_name: string | null;
    client_code: string | null;
    /** null when unassigned — no client to be active or inactive. */
    is_client_active: boolean | null;
    /** Ladder: 0 not verified, 1 PAN, 2 PAN+FA account, 3 all three. */
    verification_status: number;
    verification_label: string;
    /** Checks still to pass, in check order. Empty when unassigned or fully verified. */
    missing_verifications: string[];
    to_emails: string[];
    cc_emails: string[];
    distributor_email: string | null;
    distributor_rm_email: string | null;
    /** 'sent'/'failed' come from the SMTP conversation; 'bounced' is found later by
     * the mailbox scan. Delivery and spam-folder placement are never knowable here —
     * see the backend EmailStatus docstring. */
    email_status: SoaEmailStatus;
    email_status_label: string;
    /** The error from the last attempt, only when that attempt is what left the row
     * FAILED. Full history is in the downloadable email log, not this field. */
    last_email_error: string | null;
    send_ts: string | null;
    created_ts: string;
    pan_card?: string;
}

export type SoaReportVariant = 'two_page_disclaimer' | 'full_report';

export type SoaDisclaimerMode = 'pdf' | 'text';

/** GET/POST .../disclaimer/ — `text` is always populated (custom if saved, else the
 *  built-in default), independent of whether a PDF is also uploaded. */
export interface SoaDisclaimerStatus {
    exists: boolean;
    text: string;
    has_custom_text: boolean;
}

export interface SoaBounceScanSummary {
    /** Reports naming an SOA statement. Other senders share the mailbox and are
     *  not counted. */
    scanned: number;
    bounced: number[];
    bounced_count: number;
    /** Reports belonging to a send that has since been superseded by a re-send. */
    stale_reports: number;
}

export interface SoaDocumentListResponse {
    count: number;
    next: string | null;
    previous: string | null;
    results: SoaDocument[];
}

export interface SoaUploadSummary {
    created: number;
    updated: number;
    matched: number;
    unmatched: number;
    unmatched_reasons: Record<string, number>;
    errors: string[];
}

export type SoaAssignResponse = SoaDocument & { warning?: string };

// Sending runs as a Cloud Run job, so the response describes what was handed to it,
// not what was delivered. Live progress comes from getSendStatus.
export interface SoaSendSummary {
    queued: number;
    skipped: number;
    skipped_reasons: Record<string, number>;
    test_recipient: string | null;
    job_run_id: number | null;
}

// One SOA send runs at a time. While a run is live the Send button is locked and
// these counts drive the progress banner; null means nothing is running.
export interface SoaSendJobState {
    id: number;
    status: 'queued' | 'running';
    started_ts: string;
    started_by: string | null;
    test_recipient: string | null;
    sent: number;
    failed: number;
    total: number;
    skipped: number;
}

export interface SoaEmailTemplate {
    subject: string;
    body: string;
    available_variables: string[];
}

export interface SoaMissingClient {
    id: number;
    client_code: string;
    full_name: string;
    fa_account_no: string | null;
    email: string | null;
    is_active: boolean;
    scheme_code: string | null;
    scheme_name: string | null;
}

export interface SoaMissingClientsResponse {
    count: number;
    next: string | null;
    previous: string | null;
    results: SoaMissingClient[];
}
